import { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { EnhancedDataTable } from "@redotech/redo-web/components/ui/enhanced-data-table";
import { tableNavigationShortcuts } from "@redotech/redo-web/keyboard-shortcuts/shopper-ai-shortcuts";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { memo, useCallback, useMemo } from "react";
import { useConversationsTableContext } from "../conversations-table-context";

export const ConversationsTable = memo(function ConversationsTable({
  maxHeight = "calc(100vh - 200px)",
}: {
  maxHeight?: string;
}) {
  const {
    data,
    columns,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    focusedConversationId,
    setFocusedConversationId,
    openConversation,
    navigateUp,
    navigateDown,
    setActionMenuOpenForId,
  } = useConversationsTableContext();

  const openActionMenuForFocused = useCallback(() => {
    if (focusedConversationId) {
      setActionMenuOpenForId(focusedConversationId);
    }
  }, [focusedConversationId, setActionMenuOpenForId]);

  useKeyboardShortcuts({
    bindings: useMemo(
      () => [
        { shortcut: tableNavigationShortcuts.NAVIGATE_UP, handler: navigateUp },
        {
          shortcut: tableNavigationShortcuts.NAVIGATE_DOWN,
          handler: navigateDown,
        },
        {
          shortcut: tableNavigationShortcuts.OPEN_CONVERSATION,
          handler: openConversation,
        },
        {
          shortcut: tableNavigationShortcuts.OPEN_ACTION_MENU,
          handler: openActionMenuForFocused,
        },
      ],
      [navigateUp, navigateDown, openConversation, openActionMenuForFocused],
    ),
    scope: "conversationTable",
  });

  const getRowId = useCallback(
    (row: ConversationListItemResponse) => row.id,
    [],
  );

  const handleFocusedRowChange = useCallback(
    (rowId: string | null) => {
      setFocusedConversationId(rowId ? ConversationId.make(rowId) : null);
    },
    [setFocusedConversationId],
  );

  const handleRowClick = useCallback(
    ({ row }: { row: ConversationListItemResponse }) => {
      openConversation(row.id);
    },
    [openConversation],
  );

  return (
    <EnhancedDataTable<ConversationListItemResponse>
      columns={columns}
      data={data}
      enableInfiniteScroll
      focusedRowId={focusedConversationId}
      getRowId={getRowId}
      hasMore={hasNextPage}
      loading={isLoading}
      loadingMore={isFetchingNextPage}
      maxHeight={maxHeight}
      onFocusedRowChange={handleFocusedRowChange}
      onLoadMore={fetchNextPage}
      onRowClick={handleRowClick}
    />
  );
});
